import { reportMediaError } from './mediaErrors'
import { NETWORK_TIMEOUT_MS, withTimeout } from '@/lib/compat'
import { noteWatchProgress } from '@/lib/continue'
import { authStatus, readSession, sendKeepalive, sessionGeneration } from '@/lib/session'

/**
 * Playback bileti — «İzlə» basılanda tam filmin oynatma müqaviləsi.
 *
 * ⚠ Backend `POST /api/playback/ticket` ARTIQ HAZIRDIR (Mux imzalı manifest + DRM
 *   lisenziya serverləri). Hüquq SERVER tərəfdə yoxlanır (§14) — 403 = paywall,
 *   401 = giriş lazımdır. Player heç vaxt hüququ özü hesablamır.
 *
 * ⚠ NİYƏ `httpJson` DEYİL. `httpJson` (compat.ts) qeyri-2xx-də status DAŞIMADAN
 *   throw edir; burada isə 401/403/404 fərqi mənalıdır (giriş / paywall / hazır
 *   deyil). Ona görə `authStatus` (session.ts) işlədilir — o, status kodunu saxlayır,
 *   `Bearer` başlığını özü qoyur, token bitibsə YENİLƏYİR və 401-də bir dəfə təkrarlayır.
 *
 * ⚠ ƏVVƏL BURADA ÖZ fetch/XHR CÜTÜ VAR İDİ və token heç vaxt yenilənmirdi: access
 *   token 15 dəqiqədə bitdiyi üçün cütləşdirmədən az sonra hər film «açılmır» olurdu
 *   (treyler isə anonim uçdan işləməyə davam edirdi). Bax `session.ts` → `refreshSession`.
 */

export interface PlaybackTicket {
  /** HLS manifesti (imzalanmış). */
  manifestUrl: string
  /** DRM sistemi → lisenziya serveri (dostcasına adlar: `widevine`/`playready`/`fairplay`). */
  licenseServers?: Record<string, string> | null
  /** Lisenziya sorğusuna əlavə başlıqlar (qısa ömürlü token; hazırda backend null verir). */
  licenseHeaders?: Record<string, string> | null
  /** FairPlay sertifikatı (Safari/tvOS üçün; TV brauzerində Shaka onu işlədir). */
  fairplayCertificateUrl?: string | null
  /** «Davam et» — sonuncu yazılmış saniyə. */
  startPositionSeconds?: number | null
  /**
   * Biletin serverdə açdığı baxış sessiyası (`wch.playback_sessions`, 2026-10-25). Client-in
   * `/sessions` çağırışı onu iddia edir; oynatma heç başlamasa client onu ÖZÜ bitirir
   * (`endPlaybackSession`) — yoxsa 180 saniyə ekran sayına düşür. Köhnə API binarında yoxdur.
   */
  playbackSessionId?: string | null
}

/**
 * Oynatma biletinin nəticəsi — manifest, DRM açarları və ya rədd səbəbi
 * (hüquq yoxdur · yaş həddi · daxil olmayıb).
 */
export type PlaybackResult =
  | { status: 'country' }
  | { status: 'ok'; ticket: PlaybackTicket }
  | { status: 'unauthorized' } // 401 — giriş lazımdır
  | { status: 'forbidden' } // 403 — hüquq yoxdur (paywall)
  | { status: 'age' } // 403 — YAŞ HƏDDİ (abunə həll etmir)
  | { status: 'comingSoon' } // 403 — film hələ dərc olunmayıb (abunə həll etmir)
  | { status: 'unavailable' } // manifest yox / vimeo / şəbəkə
  | { status: 'profileMissing' } // 404 Profiles.NotFound — seçilmiş profil başqa cihazda silinib

/**
 * Bilet istəyir. Profil sahibliyi + baxış hüququ backend-də yoxlanır.
 * Şəbəkə/timeout xətası `unavailable`-a düşür — çağıran treylerə degrade edir.
 */
/**
 * @param episodeId Serial bölümü — verilibsə server `cnt.episodes.cdn_asset_id`-ni
 *   götürür və davam mövqeyini `item_type=2` açarı ilə oxuyur. Filmdə verilmir.
 */
export function requestTicket(
  contentId: string,
  profileId: string,
  episodeId?: string | null,
): Promise<PlaybackResult> {
  const body = JSON.stringify({
    contentId,
    profileId,
    deviceId: null,
    episodeId: episodeId || null,
  })

  return withTimeout(
    authStatus('/api/playback/ticket', { method: 'POST', body }),
    NETWORK_TIMEOUT_MS,
    'playback/ticket',
  )
    .then((response): PlaybackResult => {
      if (response.status !== 200) {
        if (response.status >= 500 || response.status === 404) reportMediaError('ticket-response', response.status, contentId)
        return statusResult(response.status, response.text)
      }
      try {
        return okFrom(JSON.parse(response.text))
      } catch {
        return { status: 'unavailable' }
      }
    })
    .catch((): PlaybackResult => {
      reportMediaError('ticket-network', 0, contentId)
      return { status: 'unavailable' }
    })
}

// ── Baxış sessiyası (wch.playback_sessions) ─────────────────────────────────
//
// ⚠ NİYƏ LAZIMDIR (2026-08-01-də tapılmış boşluq). Eyni anda baxış limiti
//   (`sys.settings → playback.max_concurrent_streams`) SERVERDƏ yalnız
//   `POST /api/playback/sessions`-da yoxlanılır. iOS və Android bu ucu çağırırdı,
//   TV və sayt isə YOX — yəni TV-dən baxış nə limitə sayılırdı, nə də admin
//   «Sessiyalar» monitorinqində görünürdü. Semantika iOS-dakı ilə eynidir:
//   bilet alındıqdan sonra aç, 60 saniyədə bir heartbeat, ekran söküləndə bitir.

/** `wch.playback_sessions.quality` — smallint. 3 = Full HD. */
const QUALITY_FULL_HD = 3

/** Heartbeat aralığı — iOS/Android/sayt ilə eyni (60 san). */
const HEARTBEAT_MS = 60000

/** Açıq sessiya — `close()` heartbeat-i dayandırır və sessiyanı bitirir. */
export interface PlaybackSessionHandle {
  /** Serverin sessiya id-si — biletin açdığı sətri iddia edibsə onunla eynidir. */
  id: string
  /** @param keepalive səhifə gizlənir/bağlanır — sorğu `keepalive` ilə gedir (`sendKeepalive`). */
  close: (keepalive?: boolean) => Promise<void>
}

/**
 * Sessiyanı bitirir (`POST /api/playback/sessions/{id}/end`) — best effort, heç vaxt reject etmir.
 *
 * ⚠⚠ BİLETİN SESSİYASI DA BURADAN BİTİR (2026-10-25). Bilet sessiyanı serverdə özü açır; oynatma
 *    heç başlamayanda (DRM-siz TV brauzeri, bilet gəlməmiş «Geri», Shaka dəstəklənmir → treyler)
 *    client `/sessions`-a çatmır və həmin sətir 180 saniyə ekran sayına düşürdü. ƏVVƏL veb TV
 *    biletin `playbackSessionId`-sini ümumiyyətlə oxumurdu.
 */
export function endPlaybackSession(sessionId: string, keepalive?: boolean): Promise<void> {
  const path = '/api/playback/sessions/' + sessionId + '/end'
  if (keepalive) {
    sendKeepalive(path, { method: 'POST' })
    return Promise.resolve()
  }
  return authStatus(path, { method: 'POST' }).then(
    () => undefined,
    () => undefined,
  )
}

/**
 * Sessiya SIXIŞDIRILANDA çağırılır (bax `handleFor`).
 * ⚠ Yalnız 409 + `Playback.SessionReplaced` — şəbəkə xətası bu yola DÜŞMÜR.
 */
export type PlaybackReplacedHandler = () => void

/**
 * Baxış sessiyası açır.
 *
 * ⚠ UĞURSUZLUQ OYNATMANI BLOKLAMIR — server limit dolanda ən köhnə sessiyanı özü
 *   sıxışdırır; şəbəkə xətasında isə TV-də film kəsilməməlidir. Alınmasa `null`.
 *
 * @param onReplaced sessiya başqa cihaza keçəndə (409) çağırılır — TV-də oynatma
 *   dayandırılıb səbəb göstərilməlidir (bax `handleFor`).
 */
export function openPlaybackSession(
  contentId: string,
  profileId: string,
  episodeId?: string | null,
  onReplaced?: PlaybackReplacedHandler,
): Promise<PlaybackSessionHandle | null> {
  const owner = readSession()
  const generation = sessionGeneration()
  const isCurrent = () => {
    const current = readSession()
    return generation === sessionGeneration() && (owner ? owner.userId : null) === (current ? current.userId : null)
  }
  const body = JSON.stringify({
    profileId,
    deviceId: null,
    contentId,
    episodeId: episodeId || null,
    quality: QUALITY_FULL_HD,
  })

  return withTimeout(
    authStatus('/api/playback/sessions', { method: 'POST', body }),
    NETWORK_TIMEOUT_MS,
    'playback/sessions',
  )
    .then((response): PlaybackSessionHandle | null => {
      if (!isCurrent() || response.status !== 200) return null
      let sessionId: string | null = null
      try {
        // Cavab xam JSON string-dir: "«uuid»".
        sessionId = JSON.parse(response.text) as string
      } catch {
        return null
      }
      if (!sessionId) return null
      return handleFor(sessionId, isCurrent, onReplaced)
    })
    .catch((): PlaybackSessionHandle | null => null)
}

function handleFor(sessionId: string, isCurrent: () => boolean, onReplaced?: PlaybackReplacedHandler): PlaybackSessionHandle {
  // ⚠ ÜST-ÜSTƏ DÜŞMƏ MÜHAFİZƏSİ (2026-08-01): şəbəkə yavaşlayanda heartbeat 60
  //   saniyədən uzun çəkə bilir və növbəti tick köhnəsi hələ uçarkən yenisini
  //   göndərirdi. Sorğular yığılıb brauzerin host başına 6 bağlantı limitini
  //   doldurur — ondan sonra BÜTÜN sorğular növbədə qalır, ekran «donur».
  //   İndi əvvəlkisi bitməyibsə tick sadəcə buraxılır (heartbeat itsə server
  //   sessiyanı özü köhnəlmiş sayır — məlumat itkisi yoxdur).
  let inFlight = false
  let closed = false

  const timer = setInterval(function () {
    if (!isCurrent()) {
      closed = true
      clearInterval(timer)
      return
    }
    if (inFlight) return
    inFlight = true
    void authStatus('/api/playback/sessions/' + sessionId + '/heartbeat', {
      method: 'POST',
      body: JSON.stringify({ quality: QUALITY_FULL_HD }),
    })
      .catch(function () {
        return undefined
      })
      .then(function (response) {
        inFlight = false
        /*
          ⚠⚠ 409 = SESSİYA SIXIŞDIRILIB (13 avqust 2026). Server limit dolanda ən
             köhnə sessiyanı bağlayır və bağlanmış sessiyanın heartbeat-i artıq
             `Playback.SessionReplaced` qaytarır.

             ƏVVƏL BU CAVAB UDULURDU: sayt, iOS və Android 12 avqustda
             düzəldilmişdi, VEB TV isə (hər iki skin bu faylı işlədir) hələ də
             hər cavabı atırdı — yəni televizorda film oynamağa DAVAM EDİRDİ və
             «eyni anda 2/4 ekran» fərqi TV-də faktiki deyildi.
          ⚠ YALNIZ 409: şəbəkə xətası və 5xx köhnə davranışda qalır (TV-də anlıq
            kəsinti filmi dayandırmamalıdır).
        */
        if (closed || !isCurrent() || !response || response.status !== 409) return
        if (response.text.indexOf('Playback.SessionReplaced') < 0) return
        closed = true
        clearInterval(timer)
        if (onReplaced) onReplaced()
      })
  }, HEARTBEAT_MS)

  return {
    id: sessionId,
    close: function (keepalive?: boolean) {
      if (closed) return Promise.resolve()
      closed = true
      clearInterval(timer)
      if (!isCurrent()) return Promise.resolve()
      return endPlaybackSession(sessionId, keepalive)
    },
  }
}

// ── Baxış mövqeyi (wch.watch_progress) ─────────────────────────────────────
//
// ⚠⚠ VEB TV BUNU ÜMUMİYYƏTLƏ YAZMIRDI (2026-08-07 auditi, MYTasks 547 «player
//   özəllikləri zəifdir funksional deyil»). Nəticələri ölçülə biləndir:
//     · «Davam et» sırası veb TV-dəki baxışdan XƏBƏRSİZ qalırdı — istifadəçi
//       filmi TV-də yarımçıq qoyub qayıdanda sıra boş idi;
//     · biletin `startPositionSeconds`-i heç vaxt irəliləmirdi, yəni «davam et»
//       həmişə 0-dan açırdı (ekranın öz `seekWhenReady`-si düzgün işləsə də
//       ötürüləcək dəyər yox idi).
//   Sayt (`WatchClient.tsx` → `progressIntervalSeconds: 15`) və Android TV
//   (`KinoboxPlayer.kt` → `progressIntervalMs = 15_000`) hər 15 saniyədə yazır;
//   burada da eyni aralıq və eyni gövdə işlədilir.

/** Baxış mövqeyi yazma aralığı — sayt/Android/iOS ilə eyni (15 san). */
export const PROGRESS_INTERVAL_MS = 15000

/**
 * ⚠ «BAXILDI» HƏDDİ CLIENT-DƏ HESABLANIR və üç səthdə eynidir (0.95) — server
 *   bayrağı olduğu kimi qəbul edir. Fərqli hədd qoysaq eyni film telefonda
 *   «baxılmış», TV-də «yarımçıq» görünərdi.
 */
const COMPLETED_RATIO = 0.95

/** `wch.watch_progress.item_type` — 1 = kontent (film), 2 = bölüm. */
const ITEM_TYPE_CONTENT = 1
const ITEM_TYPE_EPISODE = 2

/**
 * Baxış mövqeyini yazır (`PUT /api/me/watch-progress`).
 *
 * ⚠ POLİMORF AÇAR (`itemType` + `itemId`): serialda açar BÖLÜMÜN id-sidir, film
 *   üçün kontentin. Eyni açardan istifadə etsəydik serialın bütün bölümləri bir
 *   mövqeni üstələyərdi (Android `KinoboxClientWatch.kt`-dəki eyni izah).
 * ⚠ HEÇ VAXT REJECT ETMİR: mövqe yazısı oynatmanı bloklamamalıdır — şəbəkə
 *   düşəndə film davam etməlidir.
 */
export function reportWatchProgress(
  profileId: string,
  contentId: string,
  episodeId: string | null | undefined,
  positionSeconds: number,
  durationSeconds: number,
  keepalive?: boolean,
): Promise<void> {
  const position = Math.floor(positionSeconds)
  const duration = Math.floor(durationSeconds)
  if (position <= 0) return Promise.resolve()

  const completed = duration > 0 && position / duration >= COMPLETED_RATIO
  const body = JSON.stringify({
    profileId,
    itemType: episodeId ? ITEM_TYPE_EPISODE : ITEM_TYPE_CONTENT,
    itemId: episodeId || contentId,
    contentId,
    positionSeconds: position,
    durationSeconds: duration,
    completed,
  })

  // Serialın «Davam et»i bu bölümü açsın — izah `lib/continue.ts` → `noteWatchProgress`.
  noteWatchProgress(profileId, contentId, episodeId, completed)

  // Səhifə gizlənir/bağlanır — son yazı `keepalive` ilə (sayt `WatchClient.flushProgress`).
  if (keepalive) {
    sendKeepalive('/api/me/watch-progress', { method: 'PUT', body })
    return Promise.resolve()
  }

  return authStatus('/api/me/watch-progress', { method: 'PUT', body }).then(
    () => undefined,
    () => undefined,
  )
}

/**
 * ⚠ YAŞ 403-ü PAYWALL 403-ündən AYRILIR. İkisi də 403-dür, amma «abunə ol»
 *   mesajı yaş həddində YANILDICIDIR — abunə heç nəyi dəyişmir. Fərq
 *   ProblemDetails-in `code` sahəsindədir (`ResultExtensions.ToProblem`).
 *   `JSON.parse` sınsa paywall-a düşürük: qapı onsuz da bağlıdır.
 */
function statusResult(status: number, text?: string): PlaybackResult {
  if (status === 401) return { status: 'unauthorized' }
  /*
    2026-10-25, kod oxunuşu: seçilmiş profil başqa cihazda silinəndə bilet 404
    `Profiles.NotFound` qaytarır. ƏVVƏL bu da `unavailable`-a düşürdü və ekranda «video hələ
    hazırlanmayıb» + «Yenidən cəhd et» yazılırdı — təkrar cəhd heç vaxt kömək etmir, profil
    seçimi isə saxlancda qalırdı. Oynadıcı seçimi silib «Kim baxır?»-a aparır.
  */
  if (status === 404 && codeOf(text) === 'Profiles.NotFound') return { status: 'profileMissing' }
  if (status === 403) {
    if (codeOf(text).startsWith('Playback.Country')) return { status: 'country' }
    if (isAgeCode(text)) return { status: 'age' }
    // ⚠⚠ «TEZLİKLƏ» 403-ü PAYWALL 403-ündən AYRILIR (sahib bildirişi: «Akinak filmi
    //   samsung modelində film izlə vururam drm və ya server xətası verir ama digər
    //   filmlər açır», MYTasks 851).
    //   ÖLÇÜLDÜ 2026-08-24 canlı API-da: `akinak` üçün `isComingSoon = true` və
    //   `POST /api/playback/ticket` → 403 `Playback.ComingSoon`. Kod ayrılmadığı üçün
    //   cavab `forbidden`-ə düşürdü və ekranda «Abunəlik lazımdır» yazılırdı — halbuki
    //   abunə heç nəyi dəyişmir, film sadəcə hələ dərc olunmayıb.
    if (codeOf(text) === 'Playback.ComingSoon') return { status: 'comingSoon' }
    return { status: 'forbidden' }
  }
  return { status: 'unavailable' }
}

/** ProblemDetails-in `code` sahəsi — parse sınsa boş sətir (qapı onsuz da bağlıdır). */
function codeOf(text?: string): string {
  if (!text) return ''
  try {
    return (JSON.parse(text) as { code?: string }).code || ''
  } catch {
    return ''
  }
}

function isAgeCode(text?: string): boolean {
  const code = codeOf(text)
  return (
    code === 'Playback.AgeRestricted' ||
    code === 'Playback.ProfileAgeRestricted' ||
    code === 'Playback.BirthDateRequired'
  )
}

/** Cavab örtüyü `{ ticket: { ... } }` — manifest boşdursa oynadıla bilməz. */
function okFrom(data: unknown): PlaybackResult {
  const envelope = data as { ticket?: PlaybackTicket } | null
  const ticket = envelope ? envelope.ticket : null
  if (!ticket || !ticket.manifestUrl) return { status: 'unavailable' }
  return { status: 'ok', ticket }
}
